import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Picker from 'emoji-picker-react'

const GAP = 8
const EDGE_PADDING = 8
// Matches the .emoji-popover transition, plus a little slack.
const EXIT_DURATION = 120

// Emoji picker that floats above (or, if there's no room, below) an anchor element.
// Rendered into <body> so it isn't clipped by scrolling chat lists or hover-only toolbars.
// Stays mounted while `isOpen` is false long enough to fade out, then calls onExited.
const EmojiPopover = ({ anchorRef, isOpen, onSelect, onClose, onExited }) => {
    const popoverRef = useRef(null)
    const [position, setPosition] = useState(null)
    const [hasEntered, setHasEntered] = useState(false)

    useLayoutEffect(() => {
        const anchor = anchorRef.current
        const popover = popoverRef.current

        if (!anchor || !popover) return

        const anchorRect = anchor.getBoundingClientRect()
        // offsetWidth/Height ignore the scale() the popover animates in from.
        const width = popover.offsetWidth
        const height = popover.offsetHeight

        let top = anchorRect.top - height - GAP
        let transformOrigin = 'bottom center'

        if (top < EDGE_PADDING) {
            top = anchorRect.bottom + GAP
            transformOrigin = 'top center'
        }

        let left = anchorRect.left + anchorRect.width / 2 - width / 2

        left = Math.max(EDGE_PADDING, Math.min(left, window.innerWidth - width - EDGE_PADDING))

        setPosition({ top, left, transformOrigin })
    }, [anchorRef])

    // Wait a frame after positioning so the browser paints the start state before transitioning.
    useEffect(() => {
        if (!position) return

        const frame = requestAnimationFrame(() => setHasEntered(true))

        return () => cancelAnimationFrame(frame)
    }, [position])

    useEffect(() => {
        if (isOpen) return

        const timer = setTimeout(onExited, EXIT_DURATION)

        return () => clearTimeout(timer)
    }, [isOpen, onExited])

    useEffect(() => {
        if (!isOpen) return

        const handleMouseDown = e => {
            if (popoverRef.current && popoverRef.current.contains(e.target)) return
            if (anchorRef.current && anchorRef.current.contains(e.target)) return

            onClose()
        }

        const handleKeyDown = e => {
            if (e.key === 'Escape') onClose()
        }

        // The anchor moves when the chat scrolls or the window resizes; just close.
        const handleScroll = e => {
            if (popoverRef.current && popoverRef.current.contains(e.target)) return

            onClose()
        }

        document.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', onClose)

        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', onClose)
        }
    }, [anchorRef, isOpen, onClose])

    const isVisible = isOpen && hasEntered

    return createPortal(
        <div
            ref={popoverRef}
            className={`emoji-popover${isVisible ? ' visible' : ''}`}
            style={position ? position : { visibility: 'hidden' }}
            onClick={e => e.stopPropagation()}
        >
            <Picker
                onEmojiClick={(event, emojiObject) => onSelect(emojiObject.emoji)}
                native
            />
        </div>,
        document.body
    )
}

export default EmojiPopover
